import React, { useState } from 'react';
import { Typography, Box, Stack, Chip, Grid } from '@mui/material';
import { Search as SearchIcon, Email as EmailIcon } from '@mui/icons-material';
import LiveEditor from '../components/Playground/LiveEditor';
import AutobiaInput from '../components/Inputs/AutobiaInput';
import AutobiaSelect from '../components/Inputs/AutobiaSelect';
import AutobiaDatePicker from '../components/Inputs/AutobiaDatePicker';
import AutobiaCheckbox from '../components/Inputs/AutobiaCheckbox';
import AutobiaRadio from '../components/Inputs/AutobiaRadio';
import AutobiaSwitch from '../components/Inputs/AutobiaSwitch';
import moment from 'moment';

const Inputs = () => {
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    country: '',
    birthDate: null,
    searchQuery: '',
    newsletter: false,
    notifications: true,
    gender: '',
    darkMode: false,
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleSelectChange = (event) => {
    setFormData(prev => ({
      ...prev,
      country: event.target.value
    }));
  };

  const handleDateChange = (newValue) => {
    setFormData(prev => ({
      ...prev,
      birthDate: newValue
    }));
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleRadioChange = (event) => {
    setFormData(prev => ({
      ...prev,
      gender: event.target.value
    }));
  };

  const handleSwitchChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  // Country options
  const countryOptions = [
    { value: 'sa', label: 'Saudi Arabia' },
    { value: 'ae', label: 'United Arab Emirates' },
    { value: 'kw', label: 'Kuwait' },
    { value: 'qa', label: 'Qatar' },
    { value: 'bh', label: 'Bahrain' },
    { value: 'om', label: 'Oman' },
  ];

  // Component Examples
  const BasicInputsExample = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <AutobiaInput
          title="Email Address"
          type="email"
          placeholder="Enter your email"
          value={formData.email}
          onChange={handleInputChange('email')}
          error={errors.email}
          helperText={errors.email ? "Please enter a valid email" : ""}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AutobiaInput
          title="Password"
          type="password"
          placeholder="Enter your password"
          value={formData.password}
          onChange={handleInputChange('password')}
          error={errors.password}
          helperText={errors.password ? "Password is required" : ""}
        />
      </Grid>
    </Grid>
  );

  const InputWithIconsExample = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <AutobiaInput
          title="Search"
          placeholder="Search products..."
          value={formData.searchQuery}
          onChange={handleInputChange('searchQuery')}
          icon={<SearchIcon />}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AutobiaInput
          title="Email with Icon"
          type="email"
          placeholder="your@email.com"
          value={formData.email}
          onChange={handleInputChange('email')}
          icon={<EmailIcon />}
        />
      </Grid>
    </Grid>
  );

  const InputVariantsExample = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <AutobiaInput
          title="Default Input"
          placeholder="Default styling"
          value=""
          onChange={() => {}}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AutobiaInput
          title="Secondary Color"
          placeholder="Secondary color input"
          value=""
          onChange={() => {}}
          color="secondary"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AutobiaInput
          title="Error State"
          placeholder="Input with error"
          value=""
          onChange={() => {}}
          error={true}
          helperText="This field has an error"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AutobiaInput
          title="Disabled Input"
          placeholder="Disabled input"
          value="Cannot edit this"
          onChange={() => {}}
          disabled={true}
        />
      </Grid>
    </Grid>
  );

  const SelectExample = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <AutobiaSelect
          title="Country"
          value={formData.country}
          onChange={handleSelectChange}
          options={countryOptions}
          placeholder="Select your country"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AutobiaSelect
          title="Loading Select"
          value=""
          onChange={() => {}}
          options={[]}
          isLoading={true}
          placeholder="Loading options..."
        />
      </Grid>
    </Grid>
  );

  const DatePickerExample = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <AutobiaDatePicker
          title="Birth Date"
          value={formData.birthDate}
          onChange={handleDateChange}
          placeholder="Select your birth date"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AutobiaDatePicker
          title="Disabled Date"
          value={moment()}
          onChange={() => {}}
          placeholder="Disabled date picker"
          disabled={true}
        />
      </Grid>
    </Grid>
  );

  const CheckboxExample = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <AutobiaCheckbox
          checked={formData.newsletter}
          onChange={handleCheckboxChange('newsletter')}
          label="Subscribe to newsletter"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AutobiaCheckbox
          checked={formData.notifications}
          onChange={handleCheckboxChange('notifications')}
          label="Enable notifications"
          color="secondary"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AutobiaCheckbox
          checked={false}
          onChange={() => {}}
          label="Disabled checkbox"
          disabled={true}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AutobiaCheckbox
          checked={false}
          onChange={() => {}}
          label="Error state"
          error={true}
          helperText="This field has an error"
        />
      </Grid>
    </Grid>
  );

  const RadioExample = () => {
    const genderOptions = [
      { value: 'male', label: 'Male' },
      { value: 'female', label: 'Female' },
      { value: 'other', label: 'Other' },
      { value: 'prefer_not_to_say', label: 'Prefer not to say' },
    ];

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <AutobiaRadio
            value={formData.gender}
            onChange={handleRadioChange}
            options={genderOptions}
            label="Gender"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <AutobiaRadio
            value=""
            onChange={() => {}}
            options={genderOptions}
            label="Disabled Radio Group"
            disabled={true}
          />
        </Grid>
      </Grid>
    );
  };

  const SwitchExample = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <AutobiaSwitch
          checked={formData.darkMode}
          onChange={handleSwitchChange('darkMode')}
          label="Dark Mode"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AutobiaSwitch
          checked={true}
          onChange={() => {}}
          label="Secondary Color"
          color="secondary"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AutobiaSwitch
          checked={false}
          onChange={() => {}}
          label="Disabled Switch"
          disabled={true}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AutobiaSwitch
          checked={false}
          onChange={() => {}}
          label="Error State"
          error={true}
          helperText="This field has an error"
        />
      </Grid>
    </Grid>
  );

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 6 }}>
        <Typography variant="h2" fontWeight={700} color="gray.g700">
          Inputs & Forms
        </Typography>
        <Typography variant="body1" color="gray.g600" sx={{ maxWidth: 600 }}>
          Form input components with consistent Autobia styling, validation states, 
          and accessibility features for data collection and user interaction.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Chip label="Form Validation" color="primary" variant="outlined" />
          <Chip label="Icon Support" variant="outlined" />
          <Chip label="Error States" variant="outlined" />
        </Stack>
      </Stack>

      <Stack spacing={6}>
        {/* Basic Inputs */}
        <LiveEditor
          title="Text Inputs"
          description="Basic text inputs with labels, placeholders, and validation support."
          component={BasicInputsExample}
          code={`import AutobiaInput from './components/Inputs/AutobiaInput';

const [formData, setFormData] = useState({
  email: '',
  password: '',
});

const [errors, setErrors] = useState({});

const handleInputChange = (field) => (event) => {
  setFormData(prev => ({
    ...prev,
    [field]: event.target.value
  }));
};

const Example = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} sm={6}>
      <AutobiaInput
        title="Email Address"
        type="email"
        placeholder="Enter your email"
        value={formData.email}
        onChange={handleInputChange('email')}
        error={errors.email}
        helperText={errors.email ? "Please enter a valid email" : ""}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <AutobiaInput
        title="Password"
        type="password"
        placeholder="Enter your password"
        value={formData.password}
        onChange={handleInputChange('password')}
      />
    </Grid>
  </Grid>
);

// Props:
// title: string - Input label
// type: string - Input type (text, email, password, etc.)
// placeholder: string - Placeholder text
// error: boolean - Error state
// helperText: string - Helper/error text`}
        />

        {/* Inputs with Icons */}
        <LiveEditor
          title="Inputs with Icons"
          description="Text inputs with end icons for enhanced visual communication."
          component={InputWithIconsExample}
          code={`import AutobiaInput from './components/Inputs/AutobiaInput';
import { SearchIcon, EmailIcon } from '@mui/icons-material';

const Example = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} sm={6}>
      <AutobiaInput
        title="Search"
        placeholder="Search products..."
        value={searchQuery}
        onChange={handleInputChange('searchQuery')}
        icon={<SearchIcon />}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <AutobiaInput
        title="Email with Icon"
        type="email"
        placeholder="your@email.com"
        value={email}
        onChange={handleInputChange('email')}
        icon={<EmailIcon />}
      />
    </Grid>
  </Grid>
);

// Props:
// icon: ReactNode - Icon displayed at the end of input`}
        />

        {/* Input Variants */}
        <LiveEditor
          title="Input States & Variants"
          description="Different input states including default, error, disabled, and color variants."
          component={InputVariantsExample}
          code={`import AutobiaInput from './components/Inputs/AutobiaInput';

const Example = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} sm={6}>
      <AutobiaInput
        title="Default Input"
        placeholder="Default styling"
        value=""
        onChange={() => {}}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <AutobiaInput
        title="Secondary Color"
        placeholder="Secondary color input"
        value=""
        onChange={() => {}}
        color="secondary"
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <AutobiaInput
        title="Error State"
        placeholder="Input with error"
        value=""
        onChange={() => {}}
        error={true}
        helperText="This field has an error"
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <AutobiaInput
        title="Disabled Input"
        placeholder="Disabled input"
        value="Cannot edit this"
        onChange={() => {}}
        disabled={true}
      />
    </Grid>
  </Grid>
);

// Props:
// color: 'primary' | 'secondary'
// disabled: boolean
// error: boolean`}
        />

        {/* Select Dropdown */}
        <LiveEditor
          title="Select Dropdown"
          description="Dropdown select component with options, loading state, and Autobia styling."
          component={SelectExample}
          code={`import AutobiaSelect from './components/Inputs/AutobiaSelect';

const countryOptions = [
  { value: 'sa', label: 'Saudi Arabia' },
  { value: 'ae', label: 'United Arab Emirates' },
  { value: 'kw', label: 'Kuwait' },
  { value: 'qa', label: 'Qatar' },
];

const [country, setCountry] = useState('');

const Example = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} sm={6}>
      <AutobiaSelect
        title="Country"
        value={country}
        onChange={(e) => setCountry(e.target.value)}
        options={countryOptions}
        placeholder="Select your country"
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <AutobiaSelect
        title="Loading Select"
        value=""
        onChange={() => {}}
        options={[]}
        isLoading={true}
        placeholder="Loading options..."
      />
    </Grid>
  </Grid>
);

// Props:
// options: Array<{value, label}> - Select options
// isLoading: boolean - Loading state
// placeholder: string - Placeholder text`}
        />

        {/* Date Picker */}
        <LiveEditor
          title="Date Picker"
          description="Date selection component with Material-UI date picker integration and Autobia styling."
          component={DatePickerExample}
          code={`import AutobiaDatePicker from './components/Inputs/AutobiaDatePicker';
import moment from 'moment';

const [birthDate, setBirthDate] = useState(null);

const Example = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} sm={6}>
      <AutobiaDatePicker
        title="Birth Date"
        value={birthDate}
        onChange={setBirthDate}
        placeholder="Select your birth date"
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <AutobiaDatePicker
        title="Disabled Date"
        value={moment()}
        onChange={() => {}}
        placeholder="Disabled date picker"
        disabled={true}
      />
    </Grid>
  </Grid>
);

// Props:
// value: Date | Moment | null - Selected date (accepts both)
// onChange: function - Date change handler (returns Date object)
// disabled: boolean - Disabled state`}
        />

        {/* Checkboxes */}
        <LiveEditor
          title="Checkboxes"
          description="Checkbox components for binary choices with Autobia styling and validation states."
          component={CheckboxExample}
          code={`import AutobiaCheckbox from './components/Inputs/AutobiaCheckbox';

const [newsletter, setNewsletter] = useState(false);

const Example = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} sm={6}>
      <AutobiaCheckbox
        checked={newsletter}
        onChange={(e) => setNewsletter(e.target.checked)}
        label="Subscribe to newsletter"
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <AutobiaCheckbox
        checked={notifications}
        onChange={(e) => setNotifications(e.target.checked)}
        label="Enable notifications"
        color="secondary"
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <AutobiaCheckbox
        checked={false}
        onChange={() => {}}
        label="Disabled checkbox"
        disabled={true}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <AutobiaCheckbox
        checked={false}
        onChange={() => {}}
        label="Error state"
        error={true}
        helperText="This field has an error"
      />
    </Grid>
  </Grid>
);

// Props:
// checked: boolean - Checkbox state
// onChange: function - Change handler
// label: string - Checkbox label
// color: 'primary' | 'secondary' - Color variant
// disabled: boolean - Disabled state
// error: boolean - Error state
// helperText: string - Helper/error text
// indeterminate: boolean - Indeterminate state`}
        />

        {/* Radio Buttons */}
        <LiveEditor
          title="Radio Buttons"
          description="Radio button groups for single selection from multiple options with Autobia styling."
          component={RadioExample}
          code={`import AutobiaRadio from './components/Inputs/AutobiaRadio';

const [gender, setGender] = useState('');

const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const Example = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} sm={6}>
      <AutobiaRadio
        value={gender}
        onChange={(e) => setGender(e.target.value)}
        options={genderOptions}
        label="Gender"
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <AutobiaRadio
        value=""
        onChange={() => {}}
        options={genderOptions}
        label="Disabled Radio Group"
        disabled={true}
      />
    </Grid>
  </Grid>
);

// Props:
// value: string - Selected value
// onChange: function - Change handler
// options: Array<{value, label, disabled?}> - Radio options
// label: string - Group label
// color: 'primary' | 'secondary' - Color variant
// disabled: boolean - Disabled state
// error: boolean - Error state
// helperText: string - Helper/error text
// row: boolean - Horizontal layout`}
        />

        {/* Switches */}
        <LiveEditor
          title="Switches"
          description="Toggle switches for on/off states with smooth animations and Autobia styling."
          component={SwitchExample}
          code={`import AutobiaSwitch from './components/Inputs/AutobiaSwitch';

const [darkMode, setDarkMode] = useState(false);

const Example = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} sm={6}>
      <AutobiaSwitch
        checked={darkMode}
        onChange={(e) => setDarkMode(e.target.checked)}
        label="Dark Mode"
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <AutobiaSwitch
        checked={true}
        onChange={() => {}}
        label="Secondary Color"
        color="secondary"
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <AutobiaSwitch
        checked={false}
        onChange={() => {}}
        label="Disabled Switch"
        disabled={true}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <AutobiaSwitch
        checked={false}
        onChange={() => {}}
        label="Error State"
        error={true}
        helperText="This field has an error"
      />
    </Grid>
  </Grid>
);

// Props:
// checked: boolean - Switch state
// onChange: function - Change handler
// label: string - Switch label
// color: 'primary' | 'secondary' - Color variant
// disabled: boolean - Disabled state
// error: boolean - Error state
// helperText: string - Helper/error text
// size: 'small' | 'medium' - Switch size
// labelPlacement: 'start' | 'end' - Label position`}
        />
      </Stack>

      {/* Usage Guidelines */}
      <Box sx={{ mt: 8, p: 4, backgroundColor: 'gray.g25', borderRadius: 2 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Form Guidelines
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Validation & Feedback
            </Typography>
            <Typography variant="body2" color="gray.g600" sx={{ mb: 2 }}>
              Always provide clear error messages and validation feedback. 
              Use helperText for guidance and error states.
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Accessibility
            </Typography>
            <Typography variant="body2" color="gray.g600">
              Include proper labels, ARIA attributes, and keyboard navigation support. 
              Ensure sufficient color contrast for error states.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Layout & Spacing
            </Typography>
            <Typography variant="body2" color="gray.g600" sx={{ mb: 2 }}>
              Use consistent spacing between form elements. Group related fields 
              and provide adequate white space for readability.
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Mobile Optimization
            </Typography>
            <Typography variant="body2" color="gray.g600">
              Ensure form inputs are touch-friendly on mobile devices. 
              Use appropriate input types for better mobile keyboards.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Inputs;